import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Spinner,
} from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import filterFactory, { selectFilter } from "react-bootstrap-table2-filter";
import classnames from "classnames";
import { AvForm, AvField } from "availity-reactstrap-validation";
import moment from "moment";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import printDiv from "../../../helpers/print_data";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { postSubmitForm } from "../../../helpers/forms_helper";
import {
  ExportAvailableInCSVForReports,
  formatUnitsForReport,
} from "../../../helpers/units";
import exportFromJSON from "export-from-json";
import showNotification from "../../../helpers/show_notification";

const CorrectionReport = (props) => {
  const [activeTab, setActiveTab] = useState(
    "Purchase Order Receiving Correction",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [correctionData, setCorrectionData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pageSize, setPageSize] = useState(50);
  const requestIdRef = useRef(0);

  const [dateFrom, setDateFrom] = useState(
    moment().startOf("month").format("YYYY-MM-DD"),
  );
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));

  const correctionTypes = {
    "Purchase Order Receiving Correction": "Purchase Order",
    "Item Order Receiving Correction": "Item Order",
    "FP Order Receiving Correction": "Product Order",
    "Item Withdrawals Correction": "Item Withdraw",
    "FP Withdrawals Correction": "Product Withdraw",
  };

  function dynamicSort(property) {
    var sortOrder = 1;
    if (property[0] === "-") {
      sortOrder = -1;
      property = property.substr(1);
    }
    return function (a, b) {
      var result =
        a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
      return result * sortOrder;
    };
  }

  function buildSelectOptions(field) {
    return () => {
      const unique = [
        ...new Map(
          (correctionData || [])
            .filter((row) => row && row[field])
            .map((row) => [
              row[field],
              { value: row[field], label: row[field] },
            ]),
        ).values(),
      ];
      unique.sort(dynamicSort("label"));
      return unique;
    };
  }

  useEffect(() => {
    handleGetCorrectionByType("Purchase Order Receiving Correction");
  }, []);

  const storeNameFilter = {
    filter: selectFilter({
      options: buildSelectOptions("store_name"),
    }),
  };

  // PO columns
  const poColumns = [
    {
      dataField: "row_key",
      hidden: true,
    },
    {
      dataField: "serial",
      isDummyField: true,
      text: "#",
      formatter: (cell, row, rowIndex) => rowIndex + 1,
      headerStyle: () => ({ width: "5%" }),
    },
    {
      dataField: "po_id",
      text: "PO ID",
      sort: true,
    },
    {
      dataField: "store_name",
      text: "Store Name",
      sort: true,
      ...storeNameFilter,
    },
    {
      dataField: "item",
      text: "Item",
      sort: true,
      filter: selectFilter({
        options: buildSelectOptions("item"),
      }),
    },
    {
      dataField: "category",
      text: "Category",
      sort: true,
      filter: selectFilter({
        options: buildSelectOptions("category"),
      }),
    },
    {
      dataField: "order_qty",
      text: "Before Correction",
      sort: true,
      formatter: (cell, row) => {
        return formatUnitsForReport(row.order_qty);
      },
    },
    {
      dataField: "corrected_qty",
      text: "Correction",
      sort: true,
      formatter: (cell, row) => {
        return formatUnitsForReport(row.corrected_qty);
      },
    },
    {
      dataField: "received_qty",
      text: "After Correction",
      sort: true,
      formatter: (cell, row) => {
        return formatUnitsForReport(row.received_qty);
      },
    },
    {
      dataField: "correction_by",
      text: "Correction By",
      sort: true,
    },
    {
      dataField: "correction_date",
      text: "Correction Date",
      sort: true,
      formatter: (cell, row) => {
        return row.correction_date
          ? moment(row.correction_date).format("DD/MM/YYYY HH:mm")
          : " ";
      },
    },
  ];

  // Item Order's columns
  const itemOrderColumns = [
    {
      dataField: "row_key",
      hidden: true,
    },
    {
      dataField: "serial",
      isDummyField: true,
      text: "#",
      formatter: (cell, row, rowIndex) => rowIndex + 1,
      headerStyle: () => ({ width: "5%" }),
    },
    {
      dataField: "order_id",
      text: "Order ID",
      sort: true,
    },
    {
      dataField: "store_name",
      text: "Store Name",
      sort: true,
      ...storeNameFilter,
    },
    {
      dataField: "item",
      text: "Item",
      sort: true,
      filter: selectFilter({
        options: buildSelectOptions("item"),
      }),
    },
    {
      dataField: "category",
      text: "Category",
      sort: true,
      filter: selectFilter({
        options: buildSelectOptions("category"),
      }),
    },
    {
      dataField: "order_qty",
      text: "Before Correction",
      sort: true,
      formatter: (cell, row) => {
        return formatUnitsForReport(row.order_qty);
      },
    },
    {
      dataField: "received_qty",
      text: "After Correction",
      sort: true,
      formatter: (cell, row) => {
        return formatUnitsForReport(row.received_qty);
      },
    },
    {
      dataField: "correction_by",
      text: "Correction By",
      sort: true,
    },
    {
      dataField: "correction_date",
      text: "Correction Date",
      sort: true,
      formatter: (cell, row) => {
        return row.correction_date
          ? moment(row.correction_date).format("DD/MM/YYYY HH:mm")
          : " ";
      },
    },
  ];

  // Product Order's columns
  const productOrderColumns = [
    {
      dataField: "row_key",
      hidden: true,
    },
    {
      dataField: "serial",
      isDummyField: true,
      text: "#",
      formatter: (cell, row, rowIndex) => rowIndex + 1,
      headerStyle: () => ({ width: "5%" }),
    },
    {
      dataField: "order_id",
      text: "Order ID",
      sort: true,
    },
    {
      dataField: "store_name",
      text: "Store Name",
      sort: true,
      ...storeNameFilter,
    },
    {
      dataField: "product",
      text: "Product",
      sort: true,
      filter: selectFilter({
        options: buildSelectOptions("product"),
      }),
    },
    {
      dataField: "section",
      text: "Section",
      sort: true,
      filter: selectFilter({
        options: buildSelectOptions("section"),
      }),
    },
    {
      dataField: "order_qty",
      text: "Before Correction",
      sort: true,
      formatter: (cell, row) => {
        return formatUnitsForReport(row.order_qty);
      },
    },
    {
      dataField: "received_qty",
      text: "After Correction",
      sort: true,
      formatter: (cell, row) => {
        return formatUnitsForReport(row.received_qty);
      },
    },
    {
      dataField: "correction_by",
      text: "Correction By",
      sort: true,
    },
    {
      dataField: "correction_date",
      text: "Correction Date",
      sort: true,
      formatter: (cell, row) => {
        return row.correction_date
          ? moment(row.correction_date).format("DD/MM/YYYY HH:mm")
          : " ";
      },
    },
  ];

  // Product Withdrawl columns
  const productWithdrawlColumns = [
    {
      dataField: "row_key",
      hidden: true,
    },
    {
      dataField: "serial",
      isDummyField: true,
      text: "#",
      formatter: (cell, row, rowIndex) => rowIndex + 1,
      headerStyle: () => ({ width: "5%" }),
    },
    {
      dataField: "used_date",
      text: "Used Date",
      sort: true,
      formatter: (cell, row) => {
        return row.used_date
          ? moment(row.used_date).format("DD/MM/YYYY HH:mm")
          : " ";
      },
    },
    {
      dataField: "store_name",
      text: "Store Name",
      sort: true,
      ...storeNameFilter,
    },
    {
      dataField: "product",
      text: "Product",
      sort: true,
      filter: selectFilter({
        options: buildSelectOptions("product"),
      }),
    },
    {
      dataField: "section",
      text: "Section",
      sort: true,
      filter: selectFilter({
        options: buildSelectOptions("section"),
      }),
    },
    {
      dataField: "withdraw_qty",
      text: "Before Correction",
      sort: true,
      formatter: (cell, row) => {
        return formatUnitsForReport(row.withdraw_qty);
      },
    },
    {
      dataField: "corrected_qty",
      text: "After Correction",
      sort: true,
      formatter: (cell, row) => {
        return formatUnitsForReport(row.corrected_qty);
      },
    },
    {
      dataField: "correction_by",
      text: "Correction By",
      sort: true,
    },
    {
      dataField: "correction_date",
      text: "Correction Date",
      sort: true,
      formatter: (cell, row) => {
        return row.correction_date
          ? moment(row.correction_date).format("DD/MM/YYYY HH:mm")
          : " ";
      },
    },
  ];

  // Item Withdrawl columns
  const itemWithdrawlColumns = [
    {
      dataField: "row_key",
      hidden: true,
    },
    {
      dataField: "serial",
      isDummyField: true,
      text: "#",
      formatter: (cell, row, rowIndex) => rowIndex + 1,
      headerStyle: () => ({ width: "5%" }),
    },
    {
      dataField: "used_date",
      text: "Used Date",
      sort: true,
      formatter: (cell, row) => {
        return row.used_date
          ? moment(row.used_date).format("DD/MM/YYYY HH:mm")
          : " ";
      },
    },
    {
      dataField: "store_name",
      text: "Store Name",
      sort: true,
      ...storeNameFilter,
    },
    {
      dataField: "item",
      text: "Item",
      sort: true,
      filter: selectFilter({
        options: buildSelectOptions("item"),
      }),
    },
    {
      dataField: "category",
      text: "Category",
      sort: true,
      filter: selectFilter({
        options: buildSelectOptions("category"),
      }),
    },
    {
      dataField: "withdraw_qty",
      text: "Before Correction",
      sort: true,
      formatter: (cell, row) => {
        return formatUnitsForReport(row.withdraw_qty);
      },
    },
    {
      dataField: "corrected_qty",
      text: "After Correction",
      sort: true,
      formatter: (cell, row) => {
        return formatUnitsForReport(row.corrected_qty);
      },
    },
    {
      dataField: "correction_by",
      text: "Correction By",
      sort: true,
    },
    {
      dataField: "correction_date",
      text: "Correction Date",
      sort: true,
      formatter: (cell, row) => {
        return row.correction_date
          ? moment(row.correction_date).format("DD/MM/YYYY HH:mm")
          : " ";
      },
    },
  ];

  const getColumns = () => {
    switch (activeTab) {
      case "Purchase Order Receiving Correction":
        return poColumns;
      case "Item Order Receiving Correction":
        return itemOrderColumns;
      case "FP Order Receiving Correction":
        return productOrderColumns;
      case "Item Withdrawals Correction":
        return itemWithdrawlColumns;
      case "FP Withdrawals Correction":
        return productWithdrawlColumns;
      default:
        return [];
    }
  };

  const handleGetCorrectionByType = async (type) => {
    const requestId = ++requestIdRef.current;
    try {
      const daysDiff = moment(dateTo).diff(dateFrom, "days");
      if (daysDiff > 31) {
        setCorrectionData([]);
        setFilteredData([]);
        showNotification(
          "Total number of days cannot be more than 31.",
          "Error",
        );
        return false;
      } else if (daysDiff < 0) {
        setCorrectionData([]);
        setFilteredData([]);
        showNotification("Invalid date range.", "Error");
        return false;
      }

      setIsLoading(true);
      setCorrectionData([]);
      setFilteredData([]);
      let url =
        process.env.REACT_APP_BASEURL + "corrections/get_corrections_by_type";
      let payload = {
        correction_for: correctionTypes[type],
        date_from: dateFrom,
        date_to: dateTo,
      };
      let response = await postSubmitForm(url, payload);
      if (requestId !== requestIdRef.current) return false;
      if (response && response.status === 1) {
        const rows = (response.data || []).map((row, index) => ({
          ...row,
          row_key: `${type}_${index}_${row.po_id || row.order_id || row.batch || "row"}`,
        }));
        if (rows.length <= 0) {
          showNotification("No data found", "error");
        }
        setCorrectionData(rows);
        setFilteredData(rows);
      } else {
        showNotification((response && response.message) || "Error", "Error");
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return false;
      console.log(error);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      handleGetCorrectionByType(tab);
    }
  };

  function afterFilter(newResult) {
    setFilteredData((prev) => {
      const next = newResult || [];
      if (
        prev.length === next.length &&
        prev.every((row, index) => row === next[index])
      ) {
        return prev;
      }
      return next;
    });
  }

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      {" "}
      Showing {from} - {to} of {size} records.
    </span>
  );

  const pagination_options = {
    sizePerPage: pageSize,
    onSizePerPageChange: (sizePerPage) => {
      setPageSize(sizePerPage);
    },
    paginationSize: 5,
    pageStartIndex: 1,
    firstPageText: "First",
    prePageText: "Prev",
    nextPageText: "Next",
    lastPageText: "Last",
    nextPageTitle: "First",
    prePageTitle: "Pre page",
    firstPageTitle: "Next page",
    lastPageTitle: "Last page",
    showTotal: true,
    paginationTotalRenderer: customTotal,
    disablePageTitle: true,
    sizePerPageList: [
      { text: "50", value: 50 },
      { text: "100", value: 100 },
      { text: "200", value: 200 },
    ],
    hideSizePerPage: false,
    ignoreSinglePage: true,
  };

  const NoDataComponent = () => (
    <div className="text-center">
      {isLoading ? (
        <div className="p-3">
          <Spinner color="primary" />
          <div className="mt-2">Loading data...</div>
        </div>
      ) : (
        "No data available"
      )}
    </div>
  );

  function formatDate(date) {
    var d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  }

  const handleDownload = () => {
    const fileName = `correction_report_${activeTab
      .toLowerCase()
      .replace(/ /g, "_")}`;
    const exportType = "xls";

    let data_to_export =
      filteredData && filteredData.length ? filteredData : correctionData;

    if (data_to_export && data_to_export.length > 0) {
      var data = [];

      data_to_export.forEach(function (v) {
        switch (activeTab) {
          case "Purchase Order Receiving Correction":
            data.push({
              "PO ID": v.po_id,
              "Store Name": v.store_name,
              Item: v.item,
              Category: v.category,
              "Before Correction": ExportAvailableInCSVForReports(v.order_qty),
              Correction: ExportAvailableInCSVForReports(v.corrected_qty),
              "After Correction": ExportAvailableInCSVForReports(
                v.received_qty,
              ),
              "Correction By": v.correction_by,
              "Correction Date": v.correction_date
                ? moment(v.correction_date).format("DD/MM/YYYY HH:mm")
                : "",
            });
            break;

          case "Item Order Receiving Correction":
            data.push({
              "Order ID": v.order_id,
              "Store Name": v.store_name,
              Item: v.item,
              Category: v.category,
              "Before Correction": ExportAvailableInCSVForReports(v.order_qty),
              "After Correction": ExportAvailableInCSVForReports(
                v.received_qty,
              ),
              "Correction By": v.correction_by,
              "Correction Date": v.correction_date
                ? moment(v.correction_date).format("DD/MM/YYYY HH:mm")
                : "",
            });
            break;

          case "FP Order Receiving Correction":
            data.push({
              "Order ID": v.order_id,
              "Store Name": v.store_name,
              Product: v.product,
              Section: v.section,
              "Before Correction": ExportAvailableInCSVForReports(v.order_qty),
              "After Correction": ExportAvailableInCSVForReports(
                v.received_qty,
              ),
              "Correction By": v.correction_by,
              "Correction Date": v.correction_date
                ? moment(v.correction_date).format("DD/MM/YYYY HH:mm")
                : "",
            });
            break;

          case "Item Withdrawals Correction":
            data.push({
              "Used Date": v.used_date
                ? moment(v.used_date).format("DD/MM/YYYY HH:mm")
                : "",
              "Store Name": v.store_name,
              Item: v.item,
              Category: v.category,
              "Before Correction": ExportAvailableInCSVForReports(
                v.withdraw_qty,
              ),
              "After Correction": ExportAvailableInCSVForReports(
                v.corrected_qty,
              ),
              "Correction By": v.correction_by,
              "Correction Date": v.correction_date
                ? moment(v.correction_date).format("DD/MM/YYYY HH:mm")
                : "",
            });
            break;

          case "FP Withdrawals Correction":
            data.push({
              "Used Date": v.used_date
                ? moment(v.used_date).format("DD/MM/YYYY HH:mm")
                : "",
              "Store Name": v.store_name,
              Product: v.product,
              Section: v.section,
              "Before Correction": ExportAvailableInCSVForReports(
                v.withdraw_qty,
              ),
              "After Correction": ExportAvailableInCSVForReports(
                v.corrected_qty,
              ),
              "Correction By": v.correction_by,
              "Correction Date": v.correction_date
                ? moment(v.correction_date).format("DD/MM/YYYY HH:mm")
                : "",
            });
            break;

          default:
            break;
        }
      });

      if (data.length > 0) {
        exportFromJSON({ data, fileName, exportType });
      } else {
        showNotification("No data available to export", "Error");
      }
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title={props.t("Correction Report")} />
        <Card>
          <CardBody>
            <AvForm onValidSubmit={() => handleGetCorrectionByType(activeTab)}>
              <Row>
                <Col lg={3}>
                  <AvField
                    name="dateFrom"
                    label={props.t("From Date")}
                    placeholder={props.t("Select")}
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      setDateFrom(formatDate(selectedDate));

                      const maxDate = moment(selectedDate)
                        .add(30, "days")
                        .format("YYYY-MM-DD");
                      const currentDate = moment().format("YYYY-MM-DD");

                      if (
                        moment(dateTo).isAfter(maxDate) ||
                        moment(dateTo).isAfter(currentDate)
                      ) {
                        setDateTo(
                          maxDate > currentDate ? currentDate : maxDate,
                        );
                      }
                    }}
                    max={moment().format("YYYY-MM-DD")}
                    errorMessage={props.t("Date cannot be empty.")}
                    validate={{ required: { value: true } }}
                  />
                </Col>
                <Col lg={3}>
                  <AvField
                    name="dateTo"
                    label={props.t("To Date")}
                    placeholder={props.t("Select")}
                    type="date"
                    value={dateTo}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      setDateTo(formatDate(selectedDate));
                    }}
                    min={dateFrom}
                    max={
                      moment(dateFrom).add(30, "days").format("YYYY-MM-DD") >
                      moment().format("YYYY-MM-DD")
                        ? moment().format("YYYY-MM-DD")
                        : moment(dateFrom).add(30, "days").format("YYYY-MM-DD")
                    }
                    errorMessage={props.t("Date cannot be empty.")}
                    validate={{ required: { value: true } }}
                  />
                </Col>
                <Col lg={3}>
                  <Button
                    type="submit"
                    className="btn btn-md btn-success mt-4"
                    disabled={isLoading}
                  >
                    <i className="mdi mdi-checkbox-marked-circle"></i>{" "}
                    {props.t("Check")}
                  </Button>
                </Col>
              </Row>
            </AvForm>
          </CardBody>
        </Card>

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Nav tabs className="nav-tabs-custom w-100">
                  <div className="w-100 d-flex justify-content-center">
                    {Object.keys(correctionTypes).map((tab) => (
                      <NavItem key={tab} className="flex-fill text-center">
                        <NavLink
                          className={classnames({
                            active: activeTab === tab,
                            "nav-link": true,
                          })}
                          onClick={() => {
                            if (!isLoading) toggleTab(tab);
                          }}
                          style={{ cursor: isLoading ? "default" : "pointer" }}
                        >
                          {tab}
                        </NavLink>
                      </NavItem>
                    ))}
                  </div>
                </Nav>

                <TabContent activeTab={activeTab} className="p-3">
                  {Object.keys(correctionTypes).map((tabId) => (
                    <TabPane tabId={tabId} key={tabId}>
                      <Row className="mb-2">
                        <Col sm="4">
                          <Button
                            type="button"
                            className="btn btn-sm btn-secondary mr-2"
                            onClick={() => handleDownload()}
                          >
                            <i className="mdi mdi-file-export"></i>{" "}
                            {props.t("Export CSV")}
                          </Button>
                          <Link
                            to="#"
                            onClick={() => printDiv("printDiv")}
                            className="btn btn-sm btn-success mr-2"
                          >
                            <i className="fa fa-print"></i> Print
                          </Link>
                        </Col>
                      </Row>
                      <div id="printDiv">
                        {isLoading ? (
                          <NoDataComponent />
                        ) : (
                          <BootstrapTable
                            keyField="row_key"
                            data={correctionData}
                            columns={getColumns()}
                            striped
                            hover
                            condensed
                            filter={filterFactory({ afterFilter })}
                            pagination={paginationFactory(pagination_options)}
                            noDataIndication={() => <NoDataComponent />}
                          />
                        )}
                      </div>
                    </TabPane>
                  ))}
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default withRouter(
  connect(null, {})(withNamespaces()(CorrectionReport)),
);
